import type { SafetyCaseRecord, SafetyDashboardRow } from '../../services/reportsApi';
import { portfolioCaseStateLabel, safetyCaseCardTitle } from '../safetyCases/safetyCaseWorklist';
import { resolveSafetyCaseId, resolveSignedSafetyScanReportId } from '../../utils/acceptanceHierarchyNavigation';
import { AFTER_SIGNED_SCAN_REVIEW_SCOPE } from './afterSignedScanReview';
import { findCustomerMetric } from '../customerMetrics';

export type ReviewWorkflowSpine = {
  workflowId?: string | null;
  incidentId?: string | null;
  caseId?: string | null;
  deviceId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  riskType?: string | null;
};

/** Shown when review proceeds without a resolvable linked Safety Case workflow. */
export const RISK_PATTERN_WORKFLOW_CONTEXT_LIMITED_MODE_COPY =
  'This risk pattern is being reviewed without a linked Safety Case. Evidence review is still available. Create or link a Safety Case if this pattern requires ongoing management.';

/** Shown only when the safety dashboard lookup request fails. */
export const RISK_PATTERN_WORKFLOW_CONTEXT_FETCH_ERROR_COPY =
  'Unable to load workflow context for this Risk Pattern.';

export function hasWorkflowContextLookupKeys(
  spine: Pick<ReviewWorkflowSpine, 'workflowId' | 'caseId' | 'incidentId'>,
): boolean {
  return Boolean(
    String(spine.workflowId || '').trim()
    || String(spine.caseId || '').trim()
    || String(spine.incidentId || '').trim()
  );
}

export function isWorkflowContextFetchErrorMessage(message: string | null | undefined): boolean {
  return String(message || '').trim() === RISK_PATTERN_WORKFLOW_CONTEXT_FETCH_ERROR_COPY;
}

export type RiskPatternReviewPageState = 'state_a' | 'state_b' | 'state_c';

export type RiskPatternWorkflowReviewMode =
  | 'first_time_review'
  | 'safety_case_in_progress'
  | 'new_activity_after_signed_scan';

/** Sole next-action CTA for state B (Safety Case exists, no signed scan). */
export type RiskPatternReviewStateBPrimaryAction =
  | 'open_safety_case'
  | 'continue_review'
  | 'none';

export const RISK_PATTERN_REVIEW_PAGE_COPY = {
  state_a: {
    title: 'Review Risk Pattern',
    body: 'Review representative evidence and decide whether this Risk Pattern should become a Safety Case.',
    safetyCaseLabel: 'Not created yet',
    latestSafetyScanLabel: 'None',
    actions: {
      reviewEvidence: 'Review Evidence',
      createSafetyCase: 'Create Safety Case',
      markFalsePositive: 'Mark False Positive',
      needsMoreReview: 'Needs More Review',
    },
  },
  state_b: {
    title: 'Managed in Safety Case',
    body: 'This condition is managed in a Safety Case. Opening the Case continues management — it does not restart Investigate review.',
    bodyContinueReview:
      'Raw evidence remains available. Formal Scan verification is not performed here.',
    bodyOpenSafetyCase:
      'This condition is managed in a Safety Case. Next: continue management on the Safety Case.',
    safetyScanLabel: 'No signed scan yet',
    actions: {
      continueReview: 'Explore observations',
      openSafetyCase: 'Open Safety Case',
      openSafetyScan: 'Open Safety Scan',
    },
  },
  state_c: {
    title: 'Review new activity after latest Signed Safety Scan',
    body: 'The latest Signed Safety Scan is frozen. Review this new activity and update the Safety Case if relevant. Create an updated Safety Scan only if the new activity needs to be shared or exported.',
    actions: {
      reviewNewActivity: 'Review New Activity',
    },
  },
} as const;

export function resolveRiskPatternReviewPageState(
  context: Pick<RiskPatternWorkflowReviewContext, 'hasSafetyCase' | 'hasSignedSafetyScan'>,
): RiskPatternReviewPageState {
  if (!context.hasSafetyCase) return 'state_a';
  if (!context.hasSignedSafetyScan) return 'state_b';
  return 'state_c';
}

/**
 * Blue panel shows exactly one next action for state B.
 * An admitted Case is the persisted management decision — Open Safety Case.
 * Evidence exploration is secondary and must not restart the read loop.
 */
export function resolveRiskPatternReviewStateBPrimaryAction(args: {
  evidenceConfirmed?: boolean;
  representativeReviewComplete?: boolean;
  hasContinueReview?: boolean;
  hasOpenSafetyCase?: boolean;
}): RiskPatternReviewStateBPrimaryAction {
  void args.evidenceConfirmed;
  void args.representativeReviewComplete;
  if (args.hasOpenSafetyCase) return 'open_safety_case';
  if (args.hasContinueReview) return 'continue_review';
  return 'none';
}

export function stateBBodyCopyForPrimaryAction(
  primary: RiskPatternReviewStateBPrimaryAction,
): string {
  if (primary === 'open_safety_case') {
    return RISK_PATTERN_REVIEW_PAGE_COPY.state_b.bodyOpenSafetyCase;
  }
  if (primary === 'continue_review') {
    return RISK_PATTERN_REVIEW_PAGE_COPY.state_b.bodyContinueReview;
  }
  return RISK_PATTERN_REVIEW_PAGE_COPY.state_b.body;
}

export function formatSafetyCaseDisplayLabel(
  context: Pick<
    RiskPatternWorkflowReviewContext,
    'hasSafetyCase' | 'safetyCaseId' | 'riskPatternSummary' | 'linkedSafetyCaseTitle'
  >,
): string {
  if (!context.hasSafetyCase) return RISK_PATTERN_REVIEW_PAGE_COPY.state_a.safetyCaseLabel;
  const linkedTitle = String(context.linkedSafetyCaseTitle || '').trim();
  if (linkedTitle) {
    return linkedTitle.replace(/^Safety Case:\s*/i, '').trim() || linkedTitle;
  }
  const caseId = String(context.safetyCaseId || '').trim();
  if (caseId && context.riskPatternSummary) return `${caseId} · ${context.riskPatternSummary}`;
  return caseId || context.riskPatternSummary || 'Linked';
}

export type RiskPatternWorkflowReviewContext = {
  workflowId: string;
  riskPatternSummary: string;
  originatingRiskPattern: string;
  linkedSafetyCaseTitle: string | null;
  caseStateLabel: string | null;
  representativeIncidentId: string | null;
  reviewStatus: string | null;
  reviewStatusLabel: string | null;
  safetyCaseId: string | null;
  hasSafetyCase: boolean;
  latestSignedScanReportId: string | null;
  hasSignedSafetyScan: boolean;
  latestSignedScanAt: string | null;
  signedScanScopeStart: string | null;
  signedScanScopeEnd: string | null;
  hasNewActivityAfterSignedScan: boolean;
  latestObservationAt: string | null;
  reviewMode: RiskPatternWorkflowReviewMode;
  reviewModeLabel: string;
  pendingObservationVerificationCount?: number;
};

const WEAK_RISK_PATTERN_LABELS = new Set([
  '',
  'activity',
  'unknown',
  'event',
  'incident',
  'probe',
]);

function normalizeText(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function sanitizeRiskPatternLabel(value: string): string {
  return String(value || '')
    .replace(/\bprobe\b/gi, 'activity')
    .replace(/\s+/g, ' ')
    .trim();
}

function humanizeLabel(value?: string | null): string {
  return sanitizeRiskPatternLabel(
    String(value || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase()),
  );
}

function isMeaningfulRiskPatternLabel(value: unknown): boolean {
  const normalized = normalizeText(value);
  return !WEAK_RISK_PATTERN_LABELS.has(normalized);
}

function mapPolicyTypeToRiskPatternLabel(value?: string | null): string | null {
  const riskType = normalizeText(value);
  if (riskType === 'zone_violation' || riskType === 'restricted_zone_intrusion' || riskType === 'restricted_zone') {
    return 'Restricted-zone Activity';
  }
  if (riskType === 'near_miss' || riskType === 'policy_forklift_proximity' || riskType === 'pedestrian_in_forklift_area' || riskType === 'forklift_pedestrian') {
    return 'Pedestrian Activity in Forklift Zone';
  }
  if (riskType === 'ppe' || riskType === 'ppe_compliance' || riskType === 'ppe_violation') {
    return 'PPE Non-compliance';
  }
  if (riskType === 'blocked_pathway') {
    return 'Blocked Pathway';
  }
  if (riskType === 'working_height') {
    return 'Working at Heights';
  }
  if (!riskType || riskType === 'probe') return null;
  return humanizeLabel(value);
}

function mapHazardCategoryToRiskPatternLabel(value?: string | null): string | null {
  const hazard = normalizeText(value);
  if (!hazard || hazard === 'probe') return null;
  if (hazard.includes('restricted zone')) return 'Restricted-zone Activity';
  if (hazard.includes('ppe')) return 'PPE Non-compliance';
  if (hazard.includes('vehicle') || hazard.includes('forklift')) return 'Pedestrian Activity in Forklift Zone';
  return humanizeLabel(value);
}

export function formatRiskPatternSummary(
  row: Pick<SafetyDashboardRow, 'risk_type' | 'risk_label' | 'risk_pattern_label' | 'unsafe_condition_title' | 'hazard_category' | 'activity_type' | 'camera_id' | 'camera_label'>,
): string {
  const explicitPatternLabel = String(row.risk_pattern_label || '').trim();
  if (isMeaningfulRiskPatternLabel(explicitPatternLabel)) return sanitizeRiskPatternLabel(explicitPatternLabel);
  const unsafeConditionTitle = String(row.unsafe_condition_title || '').trim();
  if (isMeaningfulRiskPatternLabel(unsafeConditionTitle)) return sanitizeRiskPatternLabel(unsafeConditionTitle);
  const mappedPolicyLabel = mapPolicyTypeToRiskPatternLabel(row.risk_type);
  if (mappedPolicyLabel) return mappedPolicyLabel;
  const mappedHazardLabel = mapHazardCategoryToRiskPatternLabel(row.hazard_category);
  if (mappedHazardLabel) return mappedHazardLabel;
  const activityType = String(row.activity_type || '').trim();
  if (isMeaningfulRiskPatternLabel(activityType)) return sanitizeRiskPatternLabel(activityType);
  const riskType = normalizeText(row.risk_type);
  const riskLabel = normalizeText(row.risk_label);
  if (riskType === 'zone_violation' || riskType === 'restricted_zone_intrusion' || riskLabel.includes('zone')) {
    return 'Restricted-zone Activity';
  }
  if (riskType === 'pedestrian_in_forklift_area' || riskLabel === 'pedestrian in forklift area') {
    return 'Pedestrian Activity in Forklift Zone';
  }
  if (isMeaningfulRiskPatternLabel(row.risk_label)) return sanitizeRiskPatternLabel(String(row.risk_label || '').trim());
  if (isMeaningfulRiskPatternLabel(row.risk_type)) return humanizeLabel(row.risk_type);
  if (String(row.camera_label || row.camera_id || '').trim()) {
    return 'Detected recurring activity';
  }
  return 'Detected recurring activity';
}

export function hasNewActivitySinceSignedScanRow(row: SafetyDashboardRow): boolean {
  return Boolean(
    row.followup_required
    || row.has_new_activity_since_report
    || row.workflow_changed_since_signoff,
  );
}

type DashboardPendingVerificationSource = SafetyDashboardRow & {
  next_operator_action?: string | null;
  pending_review_count?: number | null;
  review?: { pending?: number | null; pending_review_count?: number | null } | null;
  semantic_metrics?: SafetyDashboardRow['semantic_metrics'];
};

function normalizeNonNegativeCount(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.trunc(n);
}

function resolveCanonicalPendingObservationVerification(
  source: DashboardPendingVerificationSource,
): number | null {
  const pendingMetric = findCustomerMetric(source.semantic_metrics || [], 'review.pending_observation_count');
  if (!pendingMetric) return null;
  return normalizeNonNegativeCount(pendingMetric.value) ?? 0;
}

/** Fail-closed: only when operator lifecycle says verify_observations AND pending > 0. */
export function resolveAuthoritativePendingObservationVerification(
  row: SafetyDashboardRow | null | undefined,
): number {
  if (!row) return 0;
  const source = row as DashboardPendingVerificationSource;
  const canonicalPending = resolveCanonicalPendingObservationVerification(source);
  if (canonicalPending != null) return canonicalPending;
  const next = String(source.next_operator_action || '').trim().toLowerCase();
  if (next !== 'verify_observations') return 0;
  const pending = normalizeNonNegativeCount(
    source.pending_review_count
    ?? source.review?.pending
    ?? source.review?.pending_review_count,
  );
  if (pending == null || pending <= 0) return 0;
  return pending;
}

export function resolveRiskPatternWorkflowReviewMode(
  row: SafetyDashboardRow,
  reviewScope?: string | null,
): RiskPatternWorkflowReviewMode {
  if (
    String(reviewScope || '').trim() === AFTER_SIGNED_SCAN_REVIEW_SCOPE
    || (resolveSignedSafetyScanReportId(row) && hasNewActivitySinceSignedScanRow(row))
  ) {
    return 'new_activity_after_signed_scan';
  }
  if (Boolean(row.finding_saved) || resolveSafetyCaseId(row)) {
    return 'safety_case_in_progress';
  }
  return 'first_time_review';
}

function reviewModeLabel(mode: RiskPatternWorkflowReviewMode): string {
  switch (mode) {
    case 'new_activity_after_signed_scan':
      return 'New activity after latest Signed Safety Scan';
    case 'safety_case_in_progress':
      return 'Safety Case in progress';
    default:
      return 'First-time Risk Pattern review';
  }
}

function safetyCaseRecordFromDashboardRow(row: SafetyDashboardRow): SafetyCaseRecord {
  return {
    safety_case_id: row.safety_case_id || row.finding_id || undefined,
    finding_id: row.finding_id || undefined,
    workflow_id: row.workflow_id || undefined,
    risk_pattern_label: row.risk_pattern_label || undefined,
    risk_type: row.risk_type || undefined,
    risk_label: row.risk_label || undefined,
    hazard_category: row.hazard_category || undefined,
    safety_case_status: row.safety_case_status || undefined,
    safety_case_status_label: row.safety_case_status_label || undefined,
    scope: {
      camera_id: row.camera_id || undefined,
      zone_id: row.zone_id || undefined,
      zones: row.zone_id ? [String(row.zone_id)] : undefined,
      risk_type: row.risk_type || undefined,
      site_id: row.site_id || undefined,
    },
  } as SafetyCaseRecord;
}

function resolveLinkedSafetyCaseTitle(row: SafetyDashboardRow, hasSafetyCase: boolean): string | null {
  if (!hasSafetyCase) return null;
  return safetyCaseCardTitle(safetyCaseRecordFromDashboardRow(row));
}

export function resolveRiskPatternWorkflowReviewContext(
  row: SafetyDashboardRow,
  options?: { reviewScope?: string | null },
): RiskPatternWorkflowReviewContext | null {
  const workflowId = String(row.workflow_id || '').trim();
  if (!workflowId) return null;

  const safetyCaseId = resolveSafetyCaseId(row) || null;
  const hasSafetyCase = Boolean(safetyCaseId);
  const latestSignedScanReportId = resolveSignedSafetyScanReportId(row) || null;
  const hasSignedSafetyScan = Boolean(latestSignedScanReportId);
  const hasNewActivityAfterSignedScan = hasSignedSafetyScan && hasNewActivitySinceSignedScanRow(row);
  const reviewMode = resolveRiskPatternWorkflowReviewMode(row, options?.reviewScope);
  const riskPatternSummary = formatRiskPatternSummary(row);

  return {
    workflowId,
    riskPatternSummary,
    originatingRiskPattern: riskPatternSummary,
    linkedSafetyCaseTitle: resolveLinkedSafetyCaseTitle(row, hasSafetyCase),
    caseStateLabel: hasSafetyCase ? portfolioCaseStateLabel(row) : null,
    representativeIncidentId: String(row.representative_incident_id || '').trim() || null,
    reviewStatus: String(row.review_status || '').trim() || null,
    reviewStatusLabel: String(row.latest_review_status_label || row.review_status_label || row.review_status || '').trim() || null,
    safetyCaseId: hasSafetyCase ? safetyCaseId : null,
    hasSafetyCase,
    latestSignedScanReportId,
    hasSignedSafetyScan,
    latestSignedScanAt: String(row.report_finalized_at || '').trim() || null,
    signedScanScopeStart: String(row.report_scope_start || '').trim() || null,
    signedScanScopeEnd: String(row.report_scope_end || '').trim() || null,
    hasNewActivityAfterSignedScan,
    latestObservationAt: String(row.source_workflow_updated_at || row.updated_at || '').trim() || null,
    reviewMode,
    reviewModeLabel: reviewModeLabel(reviewMode),
    pendingObservationVerificationCount: resolveAuthoritativePendingObservationVerification(row),
  };
}

export function findSafetyDashboardWorkflowRow(
  workflows: SafetyDashboardRow[] | null | undefined,
  workflowId: string,
): SafetyDashboardRow | null {
  const target = String(workflowId || '').trim();
  if (!target) return null;
  return (workflows || []).find((row) => String(row.workflow_id || '').trim() === target) || null;
}

export function findSafetyDashboardWorkflowRowByScope(
  workflows: SafetyDashboardRow[] | null | undefined,
  spine: ReviewWorkflowSpine,
): SafetyDashboardRow | null {
  const deviceId = String(spine.deviceId || '').trim();
  const zoneId = String(spine.zoneId || '').trim();
  const cameraId = String(spine.cameraId || '').trim();
  const riskType = String(spine.riskType || '').trim();
  if (!deviceId && !zoneId && !cameraId && !riskType) return null;

  const matches = (workflows || []).filter((row) => {
    if (deviceId && String(row.device_id || '').trim() !== deviceId) return false;
    if (zoneId && String(row.zone_id || '').trim() !== zoneId) return false;
    if (cameraId && String(row.camera_id || '').trim() !== cameraId) return false;
    if (riskType && String(row.risk_type || '').trim() !== riskType) return false;
    return true;
  });
  if (matches.length === 0) return null;

  const withCase = matches.filter((row) => Boolean(resolveSafetyCaseId(row)));
  if (withCase.length === 1) return withCase[0];

  const savedCases = withCase.filter((row) => Boolean(row.finding_saved));
  if (savedCases.length === 1) return savedCases[0];

  if (matches.length === 1) return matches[0];
  return null;
}

export function findSafetyDashboardWorkflowRowForReview(
  workflows: SafetyDashboardRow[] | null | undefined,
  spine: ReviewWorkflowSpine,
): SafetyDashboardRow | null {
  const list = workflows || [];
  const workflowId = String(spine.workflowId || '').trim();
  if (workflowId) {
    const byWorkflow = findSafetyDashboardWorkflowRow(list, workflowId);
    if (byWorkflow) return byWorkflow;
  }

  const caseId = String(spine.caseId || '').trim();
  if (caseId) {
    const byCase = list.find((row) => (
      String(row.safety_case_id || row.finding_id || '').trim() === caseId
    ));
    if (byCase) return byCase;
  }

  const incidentId = String(spine.incidentId || '').trim();
  if (incidentId) {
    const byIncident = list.find((row) => (
      String(row.representative_incident_id || row.latest_incident_id || '').trim() === incidentId
    ));
    if (byIncident) return byIncident;
  }

  return findSafetyDashboardWorkflowRowByScope(list, spine);
}

export function resolveReviewWorkflowId(
  spine: ReviewWorkflowSpine,
  row: SafetyDashboardRow | null | undefined,
): string | null {
  return String(spine.workflowId || row?.workflow_id || '').trim() || null;
}

type ReviewPathRow = SafetyDashboardRow & {
  grouped_incident_count?: number | null;
};

function buildGroupedReviewQuery(row: ReviewPathRow, fallbackSiteId: string): URLSearchParams {
  const query = new URLSearchParams();
  query.set('site_id', String(row.site_id || fallbackSiteId || '').trim());
  query.set('tab', 'open');
  query.set('table_mode', 'grouped');
  query.set('review', '1');
  if (row.zone_id) query.set('zone_id', String(row.zone_id));
  if (row.camera_id) query.set('camera_id', String(row.camera_id));
  if (row.device_id) query.set('device_id', String(row.device_id));
  if (row.risk_type) query.set('policy_type', String(row.risk_type));
  if (row.representative_incident_id) query.set('incident_id', String(row.representative_incident_id));
  if (row.workflow_id) {
    query.set('workflow_id', String(row.workflow_id));
    query.set('group_key', String(row.workflow_id));
  }

  const nextStep = String(row.recommended_next_step || '').trim().toLowerCase();
  if (!resolveSafetyCaseId(row) && !row.finding_saved) query.set('review_focus', 'risk_pattern');
  if (nextStep === 'complete_evidence_review') query.set('review_focus', 'evidence_review');
  if (nextStep === 'complete_corrective_action') query.set('review_focus', 'corrective_action');
  if (nextStep === 'complete_impact_measurement') query.set('review_focus', 'impact_measurement');
  return query;
}

export function buildRiskPatternReviewPath(row: ReviewPathRow, fallbackSiteId: string): string {
  return `/incidents?${buildGroupedReviewQuery(row, fallbackSiteId).toString()}`;
}

export type ClaimReadinessBlockerReviewFocus = 'corrective_action' | 'impact_measurement';

export function resolveClaimReadinessBlockerReviewFocus(
  blockerType: string,
): ClaimReadinessBlockerReviewFocus | null {
  const type = String(blockerType || '').trim().toLowerCase();
  if (type === 'verification_method' || type === 'field_confirmation_of_zone_controls') {
    return 'corrective_action';
  }
  if (type === 'observation_coverage_reliability' || type === 'matched_window_followup_validation') {
    return 'impact_measurement';
  }
  return null;
}

export type ClaimReadinessSafetyCaseFocus =
  | 'closure_summary_required'
  | 'contributing_condition_status_required';

export function resolveClaimReadinessSafetyCaseFocus(
  blockerType: string,
): ClaimReadinessSafetyCaseFocus | null {
  const type = String(blockerType || '').trim().toLowerCase();
  if (type === 'closure_summary_required' || type === 'contributing_condition_status_required') {
    return type;
  }
  return null;
}

export function claimReadinessBlockerActionLabel(
  blockerType: string,
  options?: { inline?: boolean },
): string {
  const inline = Boolean(options?.inline);
  const type = String(blockerType || '').trim().toLowerCase();
  if (type === 'verification_method' || type === 'field_confirmation_of_zone_controls') {
    return 'Open assign action';
  }
  if (type === 'contributing_condition_status_required') {
    return inline ? 'Go to contributing review' : 'Open contributing condition review';
  }
  if (type === 'closure_summary_required') {
    return inline ? 'Go to closure summary' : 'Open closure summary';
  }
  if (type === 'observation_coverage_reliability' || type === 'matched_window_followup_validation') {
    return 'Open track impact';
  }
  return 'Open Safety Case';
}

export function buildClaimReadinessBlockerReviewPath(args: {
  siteId: string;
  workflowId?: string | null;
  incidentId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  deviceId?: string | null;
  riskType?: string | null;
  reviewFocus: ClaimReadinessBlockerReviewFocus;
}): string {
  const query = new URLSearchParams();
  query.set('site_id', String(args.siteId || '').trim());
  query.set('tab', 'open');
  query.set('table_mode', 'grouped');
  query.set('review', '1');
  query.set('review_focus', args.reviewFocus);
  if (args.workflowId) query.set('workflow_id', String(args.workflowId));
  if (args.incidentId) query.set('incident_id', String(args.incidentId));
  if (args.zoneId) query.set('zone_id', String(args.zoneId));
  if (args.cameraId) query.set('camera_id', String(args.cameraId));
  if (args.deviceId) query.set('device_id', String(args.deviceId));
  if (args.riskType) query.set('policy_type', String(args.riskType));
  return `/incidents?${query.toString()}`;
}
